/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['modalContent', 'modalOverlay'] }] */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import queryString from 'query-string';
import cn from 'classnames';

import { CART_PAGE_MODAL } from 'constants/amethystPageTypes';
import {
  evProceedToCheckout,
  pvCart
} from 'events/cart';
import { cartModalViewEvent } from 'events/global';

import {
  fetchCartItems,
  navigateToCheckout,
  showCartModal
} from 'actions/cart';
import { track } from 'apis/amethyst';
import { pluralize } from 'helpers';
import { toUSD } from 'helpers/NumberFormats';
import { isDesktop } from 'helpers/ClientUtils';
import { onEvent } from 'helpers/EventHelpers';
import SessionRequired from 'components/common/SessionRequired';
import { SmallLoader } from 'components/Loader';
import CartItem from 'components/cart/CartItem';
import EmptyCart from 'components/cart/EmptyCart';
import CartErrors from 'components/cart/CartErrors';
import RewardsTransparency from 'components/common/RewardsTransparency';
import marketplace from 'cfg/marketplace.json';
import modalCSS from 'styles/components/common/modal.scss';
import css from 'styles/components/cart/cartModal.scss';

const { checkout: { checkoutUrl }, cart: { cartName } } = marketplace;

export class CartModal extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    router: PropTypes.object.isRequired
  };

  componentDidMount() {
    const { cart: { cartObj: cart, isLoaded }, fetchCartItems, showCartModal, cartModalViewEvent } = this.props;

    onEvent(window, 'keydown', this.onEscKeyDown);

    if (!isLoaded) {
      fetchCartItems().then(response => {
        track(pvCart, { ...response, isCartModal: true });
        cartModalViewEvent();
      });
    } else {
      track(pvCart, { ...cart, isCartModal: true });
      cartModalViewEvent();
    }

    if (!isDesktop(marketplace)) {
      setTimeout(() => {
        showCartModal(false);
      }, 5000);
    }
  }

  onEscKeyDown = event => {
    if (event.keyCode === 27) { // esc key
      this.onHideModal();
    }
  }

  onHideModal = () => {
    this.props.showCartModal(false);
  }

  onCheckingOut = e => {
    e.preventDefault();

    const { navigateToCheckout } = this.props;
    const { router } = this.context;

    this.onHideModal();
    track(evProceedToCheckout, { pageType: CART_PAGE_MODAL });
    navigateToCheckout(router);
  }

  makeEmptyCart = () => {
    const { isCustomer } = this.props;

    return (
      <EmptyCart onLinkClick={this.onHideModal} className={css.empty} isCustomer={isCustomer}>
        { !isCustomer &&
          <div className={css.signIn}>
            <Link to="/login">Sign In</Link>
          </div>
        }
      </EmptyCart>
    );
  }

  makeCartItems = () => {
    const { cart: { cartObj: { activeItems, savedItems } } } = this.props;

    return (
      <>
        <div className={css.itemContainer}>
          <CartErrors isModal={true} />
          {activeItems?.map(item =>
            <CartItem
              className={css.item}
              item={item}
              isModal={true}
              key={item.cartItemId} />
          )}

          {savedItems?.map(item =>
            <CartItem
              className={css.item}
              item={item}
              isModal={true}
              isUnavailable={true}
              key={item.cartItemId} />
          )}
        </div>

        { this.makeCartFooter() }
      </>
    );
  }

  makeHeader = () => {
    const { cart: { addedStockId } } = this.props;

    return (
      <p className={css.header}>
        { addedStockId
          ? <><span className={css.check} /> Added to {cartName}</>
          : `My ${cartName}`
        }
      </p>
    );
  }

  makeCartFooter = () => {
    const { cart: { cartCount, loading, cartObj: { subtotal } } } = this.props;
    const { testId } = this.context;

    return (
      <div className={css.footer}>
        <p className={css.subtotal}>
          <span data-test-id={testId('cartQuantity')}>{cartName} Subtotal ({cartCount} {pluralize('item', cartCount)})</span>
          <span data-test-id={testId('subtotal')}>{toUSD(subtotal.amount)}</span>
        </p>

        <form onSubmit={this.onCheckingOut} action={`${checkoutUrl}/initiate`} method="post">
          <Link
            to="/cart"
            onClick={this.onHideModal}
            className={css.secondaryCTA}
            data-test-id={testId('viewCart')}>View {cartName}</Link>

          <button
            type="submit"
            disabled={loading}
            className={css.primaryCTA}
            data-test-id={testId('proceedToCheckout')}>{ loading ? 'Updating...' : 'Proceed to Checkout'}</button>
        </form>
      </div>
    );
  };

  makeJanusPixel = () => {
    const { p13nPixelUrl, origin, cart: { addedStockId, cartObj: cart } } = this.props;
    const addedProduct = cart.activeItems.find(item => item.stockId === addedStockId);

    if (!addedProduct) {
      return null;
    }

    const { pathname, search } = window.location;
    const { stockId, styleId } = addedProduct;

    const qs = queryString.stringify({
      teen: styleId,
      child: stockId,
      widget: 'RecordCart',
      referer: `${origin}${pathname}${search}`
    });

    return (
      <SessionRequired>
        <img
          className={css.pixel}
          alt=""
          src={`${p13nPixelUrl}?${qs}`}/>
      </SessionRequired>
    );
  }

  render () {
    const { cart: { isLoaded, isModalShowing, addedStockId, cartObj: cart } } = this.props;
    const { activeItems } = cart;
    const { testId } = this.context;

    if (!isModalShowing) {
      return null;
    }

    return (
      <>
        <div className={css.container}>
          { addedStockId && this.makeJanusPixel() }
          <div className={css.modal} data-test-id={testId('cartModal')}>
            {
              !isLoaded
                ? <SmallLoader />
                : <>
                <div className={cn(modalCSS.header, css.modalHeader)}>
                  { this.makeHeader() }
                  <button
                    onClick={this.onHideModal}
                    className={modalCSS.close}
                    data-test-id={testId('closeModal')}
                    aria-label="Close"/>
                </div>
                <RewardsTransparency isForCartModal={true} />

                {
                  !activeItems.length
                    ? this.makeEmptyCart()
                    : this.makeCartItems()
                }
              </>
            }
          </div>
        </div>
        {/* eslint-disable jsx-a11y/no-static-element-interactions */}
        {/*
          react-modal does onclick on divs
          https://github.com/reactjs/react-modal/blob/master/src/components/ModalPortal.js
        */}
        <div onClick={this.onHideModal} onKeyPress={this.onHideModal} className={css.wrapper} />
      </>
    );

  }
}

export function mapStateToProps(state) {
  const { cart, cookies } = state;
  const isCustomer = !!cookies['x-main'];

  return {
    cart,
    isCustomer,
    origin: state.environmentConfig.canonical.url,
    p13nPixelUrl: `${state.environmentConfig.api.mafia.url}/janus/recos/bi/ingest/pixel.png`
  };
}

export default connect(mapStateToProps, {
  fetchCartItems,
  showCartModal,
  navigateToCheckout,
  cartModalViewEvent
})(CartModal);
